import React from 'react';
import { gettext } from '@eventbrite/i18n';

import { Link } from '@eventbrite/eds-link';

import { LogoEBrand } from '@eventbrite/eds-iconography';
import { User } from '@eventbrite/eds-iconography';
import { Mail } from '@eventbrite/eds-iconography';
import { Key } from '@eventbrite/eds-iconography';
import { ClockChunky } from '@eventbrite/eds-iconography';
import { Icon } from '@eventbrite/eds-icon';

import LinkFacebookAccountSvg from '../components/illustrations/LinkFacebookAccount';
import LinkAppleAccountSvg from '../components/illustrations/LinkAppleAccount';

import {
    LOG_IN_PATH,
    SIGN_UP_PATH,
    CHECKOUT_PATH,
    SOCIAL_SIGN_UP_PATH,
    LINK_ACCOUNTS_LOG_IN_PATH,
    RESET_PASSWORD_PATH,
    SET_PASSWORD_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
    SOCIAL_LOG_IN_PATH,
    RESET_PASSWORD_STATUS_PATH,
    SOFT_ACCOUNT_STATUS_PATH,
} from './index';

export const PASSWORDLESS_TOKEN_EXPIRED_MESSAGE = gettext(
    'Your link has expired. Please try again and we will send you a new link.',
);

export const PASSWORDLESS_TOKEN_REUSED_MESSAGE = gettext(
    'Your link has been used. Please request a new link.',
);

export const USER_HAS_ALREADY_PASSWORD_ERROR_MESSAGE = gettext(
    'The user already has a password.',
);
export const USER_ACCOUNT_SUSPENDED_ERROR_MESSAGE = gettext(
    'This user is suspended.',
);

export const INVALID_USER_ID_ERROR_MESSAGE = gettext(
    'No user exists with that ID.',
);

export const NEW_PASSWORD_TOO_WEAK_ERROR_MESSAGE = gettext(
    'New password does not meet minimum requirements',
);

export const LINKED_ACCOUNTS_SUCCESS_TITLE = gettext(
    'Your account is now linked!',
);

export const LINKED_ACCOUNTS_SUCCESS_MESSAGE = gettext(
    'You can now log into Eventbrite using your Social account. You may unlink your account any time under your Linked Accounts settings.',
);

export const EMAILS_MISMATCHES = gettext(
    "Email address doesn't match. Please try again",
);

export const ENTER_VALID_EMAIL = gettext('Please enter a valid email address');

const CHECKOUT_LOG_IN_INFO = {
    headerText: gettext('Log in to Eventbrite'),
    subtitleText: gettext('Log in for a faster checkout experience'),
    iconType: <LogoEBrand />,
    iconColor: 'white',
    haloColor: 'ui-orange',
    buttonCTA: gettext('Log in'),
};

const LOG_IN_FORM_INFO = {
    headerText: gettext('Welcome back'),
    iconType: <User />,
    subtitleText: gettext('Please enter your password to log in.'),
    buttonCTA: gettext('Log In'),
};

const LINK_ACCOUNTS_LOG_IN_FORM_INFO = {
    headerText: gettext('Link accounts'),
    iconType: <LinkFacebookAccountSvg />,
    iconColor: null,
    iconSize: null,
    subtitleText: null,
    buttonCTA: gettext('Link your accounts'),
};
const LINK_ACCOUNTS_LOG_IN_FORM_INFO_APPLE = {
    headerText: gettext('Link accounts'),
    iconType: <LinkAppleAccountSvg />,
    iconColor: null,
    iconSize: null,
    subtitleText: null,
    buttonCTA: gettext('Link your accounts'),
};

const SIGN_UP_FORM_INFO = {
    headerText: gettext('Welcome'),
    iconType: <User />,
    subtitleText: gettext('Create an account.'),
    buttonCTA: gettext('Sign Up'),
};

const SOCIAL_SIGN_UP_FORM_INFO = {
    headerText: gettext('Welcome'),
    iconType: <User />,
    subtitleText: gettext(
        'Please confirm your email and name to continue with Facebook.',
    ),
    buttonCTA: gettext('Sign Up'),
};

const SOFT_ACCOUNT_STATUS_INFO = {
    headerText: gettext('Check your email'),
    subtitleText: gettext(
        "Looks like you're already in our system! Check your email to create a new password.",
    ),
    iconType: <Mail />,
};

export const SOFT_ACCOUNT_SEND_EMAIL_FAILED =
    'There has been an error. Please try again later.';

const SET_LINKED_ACCOUNTS_PASSWORD_INFO = {
    headerText: gettext('Welcome back'),
    iconType: <User />,
    subtitleText: null,
    buttonCTA: gettext('Create Password'),
};

const RESET_PASSWORD_STATUS_INFO = {
    headerText: gettext('Check your email'),
    subtitleText: gettext(
        'We sent you an email with instructions to reset your password.',
    ),
    iconType: <Mail />,
};

const RESET_PASSWORD_INFO = {
    headerText: gettext('Reset your password'),
    iconType: <Key />,
    buttonCTA: gettext('Reset password'),
    subtitleText: gettext('Enter your new password below.'),
};

const SET_PASSWORD_INFO = {
    headerText: gettext('Create an account'),
    iconType: <User />,
    buttonCTA: gettext('Sign up'),
    subtitleText: gettext(
        "You're almost there! Enter a password to complete your account.",
    ),
};

export const EXISTING_ACCOUNT_INFO = {
    headerText: gettext('Used Eventbrite before?'),
    buttonCTA: gettext('Link account'),
    subtitleText: gettext(
        'If you have an existing Eventbrite account, enter your email and password to see all your tickets in one place.',
    ),
    yesButtonText: gettext('Yes, I have an account'),
    noButtonText: gettext("No, I'm new here"),
};

/*Should be extended to include every form/notification screen etc.*/
export const PAGE_HEADER_INFO_MAP = {
    [`${LOG_IN_PATH}Form`]: LOG_IN_FORM_INFO,
    [`${LINK_ACCOUNTS_LOG_IN_PATH}Form`]: LINK_ACCOUNTS_LOG_IN_FORM_INFO,
    [`${LINK_ACCOUNTS_LOG_IN_PATH}AppleForm`]:
        LINK_ACCOUNTS_LOG_IN_FORM_INFO_APPLE,
    [`${SIGN_UP_PATH}Form`]: SIGN_UP_FORM_INFO,
    [`${SOCIAL_SIGN_UP_PATH}Form`]: SOCIAL_SIGN_UP_FORM_INFO,
    [`${SET_LINKED_SOFT_ACCOUNTS_PATH}Form`]: SET_LINKED_ACCOUNTS_PASSWORD_INFO,
    [RESET_PASSWORD_STATUS_PATH]: RESET_PASSWORD_STATUS_INFO,
    [SOFT_ACCOUNT_STATUS_PATH]: SOFT_ACCOUNT_STATUS_INFO,
    [RESET_PASSWORD_PATH]: RESET_PASSWORD_INFO,
    [SET_PASSWORD_PATH]: SET_PASSWORD_INFO,
    [`${SOCIAL_LOG_IN_PATH}Form`]: EXISTING_ACCOUNT_INFO,
};

export const PAGE_HEADER_INFO_MAP_CHECKOUT = {
    [`${LOG_IN_PATH}Form`]: CHECKOUT_LOG_IN_INFO,
    [`${CHECKOUT_PATH}Form`]: CHECKOUT_LOG_IN_INFO,
    [`${SOCIAL_LOG_IN_PATH}Form`]: CHECKOUT_LOG_IN_INFO,
};

export const EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE = gettext(
    "This email has been used in Eventbrite before. To login, you'll need to create a password for this account",
);

export const LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML = gettext(
    "This email address is already registered with an Eventbrite account. We'll send you a magic link to connect your accounts.",
);

export const ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE = gettext(
    'Your account is connected to Facebook. Use the Facebook button to log in.',
);

export const ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE = gettext(
    'Your account is connected to Apple. Use the Apple button to log in.',
);

export const ACCOUNT_CONNECTED_TO_GOOGLE_ERROR_MESSAGE = gettext(
    'Your account is connected to Google. Use the Google button to log in.',
);

export const ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE = gettext(
    'Your account is connected to Apple and Facebook. Use the Apple or Facebook button to log in.',
);
export const ACCOUNT_CONNECTED_TO_APPLE_AND_GOOGLE_ERROR_MESSAGE = gettext(
    'Your account is connected to Apple and Google. Use the Apple or Google button to log in.',
);
export const ACCOUNT_CONNECTED_TO_GOOGLE_AND_FB_ERROR_MESSAGE = gettext(
    'Your account is connected to Google and Facebook. Use the Google or Facebook button to log in.',
);
export const ACCOUNT_CONNECTED_TO_GOOGLE_APPLE_AND_FB_ERROR_MESSAGE = gettext(
    'Your account is connected to Apple, Google and Facebook. Use the Apple, Google or Facebook button to log in.',
);
export const ACCOUNT_CONNECTED_TO_SOCIAL_ERROR_MESSAGE = gettext(
    'There is a social account associated for the provided email address. Please use the Social Button to log in.',
);

export const EMAIL_PERMISSION_DENIED_ERROR_MESSAGE = (
    <div>
        {gettext(
            'The email address associated with your Facebook account could not be retrieved. Try again and grant access to email permissions or add an email to your Facebook account. ',
        )}
        <Link
            to=" https://www.facebook.com/help/162801153783275"
            target="_blank"
            rel="noopener noreferrer"
        >
            {gettext('Learn how')}
        </Link>
    </div>
);

export const USER_ALREADY_HAS_EXTERNAL_PROVIDER_MESSAGE = gettext(
    'The user has already linked a Social account. To continue, sign in with your Eventbrite account and unlink it manually.',
);
export const TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE = gettext(
    'Unexpected error. That was weird. Try again.',
);

export const UNKNOWN_ERROR_MESSAGE = gettext(
    'Whoops! Someone must have pulled a plug somewhere... try again.',
);

export const STAFF_USER_NOT_ALLOWED_LOGIN_MESSAGE = gettext(
    'Staff user is not allowed to login using social buttons.',
);

export const UNKNOWN_EXTERNAL_PROVIDER_ERROR_MESSAGE = gettext(
    'Whoops! There was an error with your login attempt. Please try again later or choose a different login method.',
);

export const LOCKED_ACCOUNT_ERROR_MESSAGE = gettext(
    'Your Account has been locked.',
);

export const VERIFICATION_REQUIRED_ERROR_MESSAGE = gettext(
    'We don’t recognize your login details. Please continue as guest',
);

export const SUSPENDED_ACCOUNT_ERROR_MESSAGE = gettext(
    'Your Account has been suspended.',
);

export const NOT_AUTHORIZED_ERROR_MESSAGE = gettext(
    'The feature that you requested is not available to you.',
);

export const GENERIC_ERROR_MESSAGE = gettext(
    'There has been an error. Please try again later.',
);

export const MUST_CHANGE_PASSWORD_ERROR_MESSAGE = gettext(
    'This user’s password must change before logging in.',
);

export const EMAIL_UNEXISTENT_ERROR_MESSAGE = gettext(
    'We couldn’t find that email address. Please try a different one.',
);

export const EMAIL_NONEXISTENT_CONTINUE_AS_GUEST_ERROR_MESSAGE = gettext(
    'We couldn’t find an Eventbrite account associated with this address. Try again or continue as guest.',
);

export const EMAIL_SOFT_ACCOUNT_CONTINUE_AS_GUEST_ERROR_MESSAGE = gettext(
    'The account associated with your email has no set password. Continue as a guest to complete your order.',
);

export const SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE = gettext(
    'Your account is already linked with another account. Please try a different one.',
);

export const ONLY_SOCIAL_LOGIN_ERROR_MESSAGE = gettext(
    'If you’ve logged in through Facebook, you can link your Apple account in account settings',
);

export const FACEBOOK_ACCOUNT_NOT_LOGGED_IN_ERROR = gettext(
    'We couldn’t verify your account. Please continue as guest to complete your order.',
);

export const NOT_EMAIL_ASSOCIATED = gettext(
    'There is no account associated with the email.',
);

export const SIGNUP_EMAIL_EXISTS = gettext(
    'There is an account associated with the email.',
);

export const LOGIN_SOFT_ACCOUNT = gettext(
    'We sent you an email to {email} with instructions to set your password.',
);

export const CREATE_ACCOUNT = gettext('Create account');
export const GO_TO_LOGIN = gettext('Go to Log in');

export const PASSWORD_INCORRECT = gettext('The password is not correct.');

export const TOS_CREATE_ACCOUNT_MUST_BE_ACCEPTED = gettext(
    'To create account you have to accept our Terms and conditions. To continue press Create account',
);

export const TOS_SET_PASSWORD_MUST_BE_ACCEPTED = gettext(
    'To set a password you have to accept our Terms and conditions. To continue press Set Password',
);

export const TOS_SOCIAL_MUST_BE_ACCEPTED = gettext(
    'To continue you have to accept our Terms and conditions.',
);

export const TOS_PASSWORDLESS_MUST_BE_ACCEPTED = gettext(
    'To continue you have to accept our Terms and conditions.',
);

// Social error codes
export const SOCIAL_ERROR_POPUP_BLOCKED = gettext(
    'We couldn’t connect to your social account. Please disable your pop-up blocker and try again.',
);
export const SOCIAL_ERROR_UNKNOWN = gettext(
    'We couldn’t connect your accounts. Be sure to disable your pop-up blocker and turn off incognito mode. Then try again, or use a different browser.',
);
export const SOCIAL_ERROR_NOT_AUTHORIZED = gettext(
    'Sign in to your social account to connect it to Eventbrite.',
);
export const SOCIAL_ERROR_SERVER = gettext(
    'We’re having trouble connecting. Please try again.',
);

// Password reset error codes
export const PASSWORD_RESET_USED_PASSWORD = gettext(
    "Your password wasn't changed. New password matches one of last 4 used passwords",
);
export const PASSWORD_TOKEN_EXPIRED_MESSAGE = gettext(
    'Your password reset token has expired. Please try again.',
);
export const TOKEN_USED_OR_EXPIRED_MESSAGE = gettext(
    'This link has expired or has already been used',
);
export const PASSWORD_RESET_RESTRICTED_ACCOUNT = gettext(
    'Your account is locked. Please contact support for assistance.',
);

export const TEXT_TOKEN_EXPIRES_TIME = (
    <p className="eds-text--center eds-text-color--grey-600 eds-text-bm eds-l-mar-hor-3">
        <Icon size="small" type={<ClockChunky />} color="grey-600" />
        {gettext('For your security, the link expires in 15 minutes')}
    </p>
);
export const TEXT_LIMIT_RETRY_TIME = (
    <p className="eds-text--center eds-text-color--grey-600 eds-text-bm eds-l-mar-hor-3">
        <Icon size="small" type={<ClockChunky />} color="grey-600" />
        {gettext(
            'Please wait 1 hour before requesting a new login link to try again',
        )}
    </p>
);

export const TEXT_TOKEN_USED_ONCE = (
    <div className="eds-text--center eds-text-color--grey-600 eds-text-bm eds-l-mar-hor-3">
        <Icon size="small" type={<ClockChunky />} color="grey-600" />
        {gettext('Links can only be used once and expire after 2 hours.')}
    </div>
);
